import React, { useEffect, useRef, useState } from "react";
import { userApi } from "@/lib/userApi";
import { Avatar } from "@/components/community/ChatList";
import { pulseField } from "@/lib/pulse";

/** Campo con autocompletado de menciones: al escribir @ sugiere miembros. */
export const MentionInput = ({ value, onChange, onMentions, multiline = false,
  placeholder, className = "", testId, onEnter, dropUp = false }) => {
  const [list, setList] = useState(null);
  const [term, setTerm] = useState(null);
  const picked = useRef(new Map());
  const ref = useRef(null);

  useEffect(() => {
    if (term === null) return;
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await userApi.get("/community/members", { params: { q: term } });
        if (alive) setList(data.slice(0, 6));
      } catch { if (alive) setList([]); }
    }, 180);
    return () => { alive = false; clearTimeout(t); };
  }, [term]);

  const handle = (e) => {
    const text = e.target.value;
    onChange(text);
    const match = /(?:^|\s)@([\p{L}\p{N}._-]{0,20})$/u.exec(text.slice(0, e.target.selectionEnd));
    if (match) setTerm(match[1]);
    else { setTerm(null); setList(null); }
    // conserva solo las menciones cuyo nombre sigue escrito
    const keep = [...picked.current.entries()].filter(([, name]) => text.includes(`@${name}`));
    picked.current = new Map(keep);
    onMentions?.(keep.map(([key]) => key));
  };

  const choose = (person) => {
    const name = person.name.replace(/\s+/g, "_");
    const next = value.replace(/(^|\s)@[\p{L}\p{N}._-]{0,20}$/u, `$1@${name} `);
    picked.current.set(person.key, name);
    onChange(next.endsWith(" ") ? next : `${next} `);
    onMentions?.([...picked.current.keys()]);
    setList(null); setTerm(null);
    ref.current?.focus();
  };

  const common = {
    ref,
    value,
    onChange: handle,
    placeholder,
    "data-testid": testId,
    onKeyDown: (e) => {
      pulseField(e.currentTarget);
      if (e.key === "Enter" && !e.shiftKey && !multiline) { e.preventDefault(); onEnter?.(); }
      if (e.key === "Escape") { setList(null); setTerm(null); }
    },
    className: `glass-field w-full px-3.5 py-2.5 rounded-xl text-[15px] text-white placeholder-white/35 outline-none ${multiline ? "resize-none" : ""} ${className}`,
  };

  return (
    <div className="relative flex-1">
      {multiline ? <textarea rows={2} {...common} /> : <input {...common} />}
      {list && list.length > 0 && (
        <div className={`absolute left-0 ${dropUp ? "bottom-full mb-2" : "top-full mt-2"} glass-card p-1.5 w-64 z-40`}
          data-testid="mention-suggestions">
          {list.map((p) => (
            <button key={p.key} data-testid={`mention-${p.key}`} onClick={() => choose(p)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/8 text-left">
              <Avatar person={p} size={26} />
              <span className="text-xs text-white truncate">{p.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** Resalta las @menciones dentro de un texto. */
export const RichText = ({ text }) => (
  <>
    {String(text || "").split(/(@[\p{L}\p{N}._-]+)/u).map((part, i) =>
      part.startsWith("@")
        ? <span key={`m-${i}-${part}`} className="mention-chip">{part}</span>
        : <span key={`t-${i}-${part}`}>{part}</span>)}
  </>
);

export default MentionInput;
